import { XMarkIcon } from '@heroicons/react/16/solid';
import { ArrowDownTrayIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { Key, useCallback, useMemo, useState } from 'react';

import { SamplingUploadedFile } from 'src/shared/types/sampling';
import { isImageExtension } from 'src/shared/utils/fileTypeUtils';

import { cn, Image } from 'src/shared/components/ui';
import {
  ButtonV2,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography
} from 'src/shared/components/ui';

const FILTER_TAGS = [
  { key: 'qa-report', label: 'QA Report' },
  { key: 'invoice', label: 'Invoice' }
];

const fileColumns = [
  { key: 'file', label: '' },
  { key: 'action', label: '' }
];

/** The fields FilesTab shows, common to every uploaded file shape. */
type FilesTabFile = Pick<
  SamplingUploadedFile,
  'id' | 'url' | 'name' | 'description' | 'tags'
>;

interface FilesTabProps {
  files: FilesTabFile[] | null;
}

const FilesTab: React.FC<FilesTabProps> = ({ files }) => {
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

  const visibleFiles = useMemo(
    () =>
      files?.filter((file) => !file.tags.includes('visibility:hidden')) ?? [],
    [files]
  );

  const availableFilterTags = useMemo(() => {
    return FILTER_TAGS.filter((tag) =>
      visibleFiles.some((file) => file.tags.includes(tag.key))
    );
  }, [visibleFiles]);

  const filteredFiles = useMemo(() => {
    if (selectedTags.size === 0) {
      return visibleFiles;
    }

    return visibleFiles.filter((file) =>
      Array.from(selectedTags).some((tag) => file.tags.includes(tag))
    );
  }, [visibleFiles, selectedTags]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }

      return newSet;
    });
  };

  const renderCell = useCallback((item: FilesTabFile, columnKey: Key) => {
    const fileIsImage = isImageExtension(item.name);

    switch (columnKey) {
      case 'file':
        return (
          <div className="flex items-center gap-2">
            <div className="size-12 flex items-center justify-center object-cover min-w-12 rounded-xl bg-gray-2 border border-gray-4 overflow-hidden">
              {fileIsImage ? (
                <Image
                  disableAnimation
                  disableSkeleton
                  className="size-12 object-cover rounded-xl"
                  src={item.url}
                  alt={item.name}
                />
              ) : (
                <DocumentIcon className="size-6" />
              )}
            </div>
            <div className="flex flex-col">
              <Typography variant="body-1" weight="medium">
                {item.name}
              </Typography>
              {item.description && (
                <Typography
                  variant="body-3"
                  weight="medium"
                  className="text-secondary-text"
                >
                  {item.description}
                </Typography>
              )}
            </div>
          </div>
        );

      case 'action':
        return (
          <ButtonV2
            onClick={() => window.open(item.url, '_blank')}
            size="md"
            isIconOnly
            color="secondary"
            className="group-hover:!bg-gray-4"
          >
            <ArrowDownTrayIcon className="size-4" />
          </ButtonV2>
        );

      default:
        return '';
    }
  }, []);

  if (visibleFiles.length === 0) {
    return (
      <div className="px-5 pb-5">
        <Typography variant="body-1" className="text-secondary-text">
          No files available
        </Typography>
      </div>
    );
  }

  return (
    <div className="px-5 pt-2 pb-5 flex flex-col gap-4">
      {availableFilterTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {availableFilterTags.map((tag) => (
            <ButtonV2
              key={tag.key}
              onClick={() => toggleTag(tag.key)}
              color="tertiary"
              size="sm"
              className={cn(
                'font-semibold',
                selectedTags.has(tag.key)
                  ? 'border-black text-black'
                  : 'text-secondary-text'
              )}
            >
              {tag.label}
            </ButtonV2>
          ))}
          {selectedTags.size > 0 && (
            <ButtonV2
              color="fourth"
              size="sm"
              className="font-semibold"
              onClick={() => setSelectedTags(new Set())}
              startContent={<XMarkIcon className="size-4" />}
            >
              Clear
            </ButtonV2>
          )}
        </div>
      )}

      {filteredFiles.length === 0 ? (
        <Typography variant="body-1" className="text-secondary-text">
          No files match the selected filters
        </Typography>
      ) : (
        <Table aria-label="Files table" hideHeader>
          <TableHeader columns={fileColumns}>
            {(column) => (
              <TableColumn className="px-4" key={column.key}>
                {column.label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody items={filteredFiles}>
            {(item) => (
              <TableRow
                onClick={() => window.open(item.url, '_blank')}
                className="group cursor-pointer"
                key={item.id}
              >
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default FilesTab;
